/**
 * Hotel Traveller Details API
 *
 * Handles traveller add/edit/select/deselect operations via CBackend.
 * CBackend forwards to Agent API with api_call_identifier = HOTEL_TRAVELLER_DETAILS.
 *
 * Backend is the single source of truth - frontend should NEVER derive traveller state locally.
 *
 * Operations:
 * - hotel_traveller_detail_add: Add a new traveller
 * - hotel_traveller_detail_edit: Edit existing traveller
 * - hotel_traveller_detail_select: Select a traveller
 * - hotel_traveller_detail_deselect: Deselect a traveller
 */
import type {
  HotelSavedTraveller,
  TravellerDetailActionType,
  TravellerDetailRequest,
} from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { generateUUID } from '@/shared/utils/uuid';
import { baseFetch, createApiError, getCBContext } from './baseApi';

// ============================================
// Error Class
// ============================================

export const TravellerDetailsError = createApiError('TravellerDetailsError');

// ============================================
// Response Type
// ============================================

export interface TravellerDetailsApiResponse {
  type: 'hotels-traveller-details';
  travellers: HotelSavedTraveller[];
  success: boolean;
  error?: { code: string; message: string };
}

// ============================================
// Request Types
// ============================================

export interface TravellerDetailsRequestParams {
  /** Session ID */
  sessionId: string;
  /** User ID */
  userId: string;
  /** Transaction key from review response */
  transactionKey: string;
  /** Action type */
  actionType: TravellerDetailActionType;
  /** Traveller details for add/edit operations */
  travellerDetails: TravellerDetailRequest[];
}

// ============================================
// Raw API Response Types
// ============================================

interface RawCBackendResponse {
  success?: boolean;
  travellers?: HotelSavedTraveller[];
  errorCode?: string;
  errorMessage?: string;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the CBackend request payload for traveller details operations
 */
function buildCBackendPayload(params: TravellerDetailsRequestParams): Record<string, unknown> {
  const context = getCBContext();

  return {
    request_id: generateUUID(),
    conversation_id: generateUUID(),
    uuid: context.uuid,
    auth: context.authToken,
    device_info: {
      device_id: context.deviceId,
      device_type: 'DESKTOP',
      app_ver: '1.21.1',
      platform: 'DESKTOP',
    },
    rephrased_query: getQueryForAction(params.actionType),
    intents: [],
    action_type: params.actionType,
    request_json: {
      transactionKey: params.transactionKey,
      travellerDetails: params.travellerDetails,
    },
  };
}

/**
 * Get appropriate query string for the action type
 */
function getQueryForAction(actionType: TravellerDetailActionType): string {
  switch (actionType) {
    case 'hotel_traveller_detail_add':
      return 'Add new traveller';
    case 'hotel_traveller_detail_edit':
      return 'Edit traveller';
    case 'hotel_traveller_detail_select':
      return 'Select traveller';
    case 'hotel_traveller_detail_deselect':
      return 'Deselect traveller';
    default:
      return 'Traveller operation';
  }
}

// ============================================
// API Function
// ============================================

/**
 * Call traveller details CBackend API for add/edit/select/deselect operations.
 * POST /hotels/traveller-details?sessionId=...&userId=...
 *
 * CBackend forwards to Agent API with api_call_identifier = HOTEL_TRAVELLER_DETAILS.
 *
 * IMPORTANT: The response contains the complete updated list of travellers.
 * Frontend MUST override its local state with this response - no local computation.
 */
export async function callTravellerDetailsApi(
  params: TravellerDetailsRequestParams,
): Promise<TravellerDetailsApiResponse> {
  const emptyResponse: TravellerDetailsApiResponse = {
    type: 'hotels-traveller-details',
    travellers: [],
    success: false,
    error: { code: 'UNKNOWN_ERROR', message: 'Unknown error occurred' },
  };

  const payload = buildCBackendPayload(params);

  logger.warn('[TravellerDetailsApi] Calling CBackend API:', {
    actionType: params.actionType,
    travellerCount: params.travellerDetails.length,
  });

  const { data, error } = await baseFetch<RawCBackendResponse>({
    endpoint: HOTELS_ENDPOINTS.TRAVELLER_DETAILS,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'TravellerDetailsApi',
  });

  // Network/HTTP error
  if (error) {
    logger.error('[TravellerDetailsApi] Network error:', error);
    return { ...emptyResponse, error };
  }

  // API returned error
  if (data?.errorCode ?? data?.errorMessage) {
    const apiError = {
      code: data.errorCode ?? 'API_ERROR',
      message: data.errorMessage ?? 'Unknown API error',
    };
    logger.error('[TravellerDetailsApi] API error:', apiError);
    return { ...emptyResponse, error: apiError };
  }

  // Check success flag
  if (data?.success === false) {
    const apiError = {
      code: data.errorCode ?? 'OPERATION_FAILED',
      message: data.errorMessage ?? 'Traveller operation failed',
    };
    logger.error('[TravellerDetailsApi] Operation failed:', apiError);
    return { ...emptyResponse, error: apiError };
  }

  // Extract travellers from response
  const travellers = data?.travellers ?? [];

  logger.info('[TravellerDetailsApi] Success:', { travellerCount: travellers.length });

  return {
    type: 'hotels-traveller-details',
    travellers,
    success: true,
  };
}

// ============================================
// Convenience Functions
// ============================================

/**
 * Add a new traveller
 */
export async function addTraveller(
  params: Omit<TravellerDetailsRequestParams, 'actionType'>,
): Promise<TravellerDetailsApiResponse> {
  return callTravellerDetailsApi({
    ...params,
    actionType: 'hotel_traveller_detail_add',
  });
}

/**
 * Edit an existing traveller
 */
export async function editTraveller(
  params: Omit<TravellerDetailsRequestParams, 'actionType'>,
): Promise<TravellerDetailsApiResponse> {
  return callTravellerDetailsApi({
    ...params,
    actionType: 'hotel_traveller_detail_edit',
  });
}

/**
 * Select a traveller
 */
export async function selectTravellerApi(
  params: Omit<TravellerDetailsRequestParams, 'actionType'>,
): Promise<TravellerDetailsApiResponse> {
  return callTravellerDetailsApi({
    ...params,
    actionType: 'hotel_traveller_detail_select',
  });
}

/**
 * Deselect a traveller
 */
export async function deselectTravellerApi(
  params: Omit<TravellerDetailsRequestParams, 'actionType'>,
): Promise<TravellerDetailsApiResponse> {
  return callTravellerDetailsApi({
    ...params,
    actionType: 'hotel_traveller_detail_deselect',
  });
}
